"use client";

import { useState, useEffect, useRef } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../config/firebase";
import { useAuth } from "../AuthContext";
import { PAYPAL_CONFIG, validatePayPalConfig } from "../config/paypalConfig";
import { createPavilionPayPalOrder, capturePavilionPayPalOrder } from "../services/paypalService";
import { useCart } from "../context/CartContext";
import "../css/ReservationForm.css";
import "../css/Cart.css";

const parsePrice = (v) => {
  const n = parseFloat(String(v ?? 0).replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

// Mirrors functions/index.js's PAVILION_MIN_GUESTS — keyed by the stable
// Firestore doc id (set in PavillionCard.jsx's DEFAULT_EVENTS), not the
// admin-editable title, so renaming an experience can't break this.
const MIN_GUESTS_BY_ID = {
  "pavilion-1": 6, // Legacy Lounge: Baldwin & Bubbles
  "pavilion-2": 6, // Civic Spirit: Hamilton Pour
  "pavilion-3": 6, // Island Revival: Garifuna Gold
  "pavilion-4": 4, // Prime Roots: Agape Garden Table
  "pavilion-5": 8, // The American Dream: Tubman
  "pavilion-6": 10, // Liberation Fire: Maroon Feast
};

export default function PBPavilionReservationForm() {
  const { currentUser, authReady } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const passedExperience = location.state?.experience || "";
  const cartItem = location.state?.cartItem || null;

  const [events, setEvents] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    guestName2: "",
    age1: "",
    age2: "",
    date: "",
    email: "",
    phone: "",
    guests: 2,
    experience: "",
    note: "",
  });
  const { addItem, items: cartItems } = useCart();

  const [step, setStep] = useState("form"); // "form" | "payment"
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);
  const [cartAdded, setCartAdded] = useState(false);
  const paymentStarted = useRef(false);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const snap = await getDocs(collection(db, "pavilionEvents"));
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        data.sort((a, b) => a.id.localeCompare(b.id));
        setEvents(data);
      } catch (error) {
        console.error("Error fetching PB Pavilion events:", error);
      }
    };
    fetchEvents();
  }, []);

  useEffect(() => {
    if (passedExperience) {
      const match = events.find((ev) => ev.title === passedExperience || ev.id === passedExperience);
      if (match) {
        setFormData((prev) => ({ ...prev, experience: match.id }));
      }
    }
  }, [passedExperience, events]);

  // Pre-fill form when navigated from Cart's "Book This Item" button
  useEffect(() => {
    if (!cartItem || events.length === 0) return;
    setFormData((prev) => ({
      ...prev,
      name: cartItem.primaryGuest || prev.name,
      guestName2: cartItem.guestName2 || prev.guestName2,
      date: cartItem.date || prev.date,
      email: cartItem.email || prev.email,
      phone: cartItem.phone || prev.phone,
      guests: cartItem.guests || prev.guests,
      experience: cartItem.itemId || prev.experience,
      note: cartItem.note || prev.note,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItem, events.length]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "guests" ? parseInt(value, 10) : value,
    }));
  };

  const selectedEvent = events.find((ev) => ev.id === formData.experience);
  const currentMin = MIN_GUESTS_BY_ID[formData.experience] || 1;
  const MAX_GUESTS = 20;
  const guestOptions = Array.from(
    { length: MAX_GUESTS - currentMin + 1 },
    (_, i) => currentMin + i
  );

  // Whenever the experience (and thus its minimum) changes, snap the guest
  // count back to that minimum so it's always a valid option in the dropdown.
  useEffect(() => {
    setFormData((prev) => ({ ...prev, guests: currentMin }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.experience]);

  const resetForm = () => {
    paymentStarted.current = false;
    setFormData({
      name: "",
      guestName2: "",
      age1: "",
      age2: "",
      date: "",
      email: "",
      phone: "",
      guests: 2,
      experience: "",
      note: "",
    });
    setStep("form");
  };

  const handleAddToCart = () => {
    setSubmitMessage(null);
    if (!selectedEvent) {
      setSubmitMessage({ type: "error", text: "Please select a PB Pavilion experience." });
      return;
    }
    if (formData.guests < currentMin) {
      setSubmitMessage({ type: "error", text: `Minimum of ${currentMin} guests required for this experience.` });
      return;
    }
    if (!formData.name.trim()) {
      setSubmitMessage({ type: "error", text: "Please enter the primary guest name." });
      return;
    }
    if (!formData.date) {
      setSubmitMessage({ type: "error", text: "Please select a date." });
      return;
    }
    if (!formData.email.trim()) {
      setSubmitMessage({ type: "error", text: "Please enter your email address." });
      return;
    }

    const cartItemId = `pavilion-${selectedEvent.id}-${formData.date}`;

    if (cartItems.some((i) => i.cartItemId === cartItemId)) {
      setSubmitMessage({ type: "info", text: "This experience for that date is already in your cart." });
      return;
    }

    addItem({
      cartItemId,
      category: "pavilion",
      itemId: selectedEvent.id,
      itemName: selectedEvent.title,
      price: selectedEvent.price || "",
      priceNumeric: parsePrice(selectedEvent.price),
      checkIn: null,
      checkOut: null,
      nights: null,
      date: formData.date,
      guests: formData.guests,
      minGuests: currentMin,
      primaryGuest: formData.name,
      guestName2: formData.guestName2,
      email: formData.email,
      phone: formData.phone,
      note: formData.note,
      addedAt: Date.now(),
    });

    setCartAdded(true);
    setSubmitMessage({ type: "success", text: "Added to your cart!", link: { label: "View Cart →", to: "/cart" } });
    setTimeout(() => { setCartAdded(false); setSubmitMessage(null); }, 5000);
  };

  const handleContinueToPayment = (e) => {
    e.preventDefault();
    setSubmitMessage(null);

    if (!selectedEvent) {
      setSubmitMessage({ type: "error", text: "Please select a PB Pavilion experience." });
      return;
    }
    if (formData.guests < currentMin) {
      setSubmitMessage({
        type: "error",
        text: `Minimum of ${currentMin} guests required for this experience.`,
      });
      return;
    }

    setStep("payment");
  };

  if (!authReady) {
    return null;
  }

  return (
    <div style={{ position: "relative", minHeight: "100vh", paddingBottom: "60px" }}>
      <video
        autoPlay
        muted
        loop
        playsInline
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          objectFit: "cover",
          zIndex: -2,
        }}
      >
        <source src="/videos/PavilionVid.mp4" type="video/mp4" />
      </video>

      <div className="reservation-form-container pavilion">
        <h2>PB Pavilion Reservation</h2>

        {!currentUser ? (
          <p style={{ textAlign: "center", color: "white", position: "relative", zIndex: 2 }}>
            You'll need an account to book a PB Pavilion experience.{" "}
            <Link to="/sign-in" style={{ color: "white", textDecoration: "underline", fontWeight: 700 }}>
              Sign in
            </Link>{" "}
            or{" "}
            <Link to="/sign-up" style={{ color: "white", textDecoration: "underline", fontWeight: 700 }}>
              create an account
            </Link>{" "}
            to continue.
          </p>
        ) : (
          <>
            {step === "form" && (
              <form className="reservation-form" onSubmit={handleContinueToPayment}>
                {/* 1. Guest names */}
                <div className="guest-names-row">
                  <div className="form-group">
                    <label htmlFor="name">Primary Guest Name</label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Enter primary guest name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      autoComplete="off"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="guestName2">Companion Guest Name </label>
                    <input
                      id="guestName2"
                      name="guestName2"
                      type="text"
                      placeholder="Enter companion guest name"
                      value={formData.guestName2}
                      onChange={handleChange}
                      autoComplete="off"
                    />
                  </div>
                </div>

                {/* 2. Ages */}
                <div className="age-row">
                  <div className="form-group">
                    <label htmlFor="age1">Age – Primary Guest</label>
                    <input
                      id="age1"
                      name="age1"
                      type="number"
                      placeholder="Enter age"
                      value={formData.age1}
                      onChange={handleChange}
                      min={1}
                      required
                      autoComplete="off"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="age2">Age – Companion Guest</label>
                    <input
                      id="age2"
                      name="age2"
                      type="number"
                      placeholder="Enter age"
                      value={formData.age2}
                      onChange={handleChange}
                      min={1}
                      autoComplete="off"
                    />
                  </div>
                </div>

                {/* 3. Date */}
                <label htmlFor="date">Date</label>
                <input
                  id="date"
                  name="date"
                  type="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                />

                {/* 4. Contact Info */}
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter email"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="off"
                  required
                />

                <label htmlFor="phone">Phone Number</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="Enter phone number"
                  value={formData.phone}
                  onChange={handleChange}
                  autoComplete="off"
                  required
                />

                {/* 5. Guests */}
                <label htmlFor="guests">
                  Number of Guests {formData.experience ? `(min ${currentMin})` : ""}
                </label>
                <select
                  id="guests"
                  name="guests"
                  value={formData.guests}
                  onChange={handleChange}
                  required
                >
                  {guestOptions.map((count) => (
                    <option key={count} value={count}>
                      {count} guest{count === 1 ? "" : "s"}
                    </option>
                  ))}
                </select>

                {/* 6. Experience selection */}
                <label htmlFor="experience">Select PB Pavilion Experience</label>
                <select
                  id="experience"
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>
                    Select PB Pavilion experience...
                  </option>
                  {events.filter((e) => e.available !== false).map((event) => (
                    <option key={event.id} value={event.id}>
                      {event.title} — {event.price ? `$${event.price}+ PER PERSON` : ""}
                    </option>
                  ))}
                </select>

                {/* 7. Special Requests */}
                <label htmlFor="note">Special Requests</label>
                <textarea
                  id="note"
                  name="note"
                  placeholder="Add any special requests (optional)"
                  value={formData.note}
                  onChange={handleChange}
                  rows={3}
                />

                <button type="submit">Continue to Payment</button>
                <button
                  type="button"
                  className={`btn-add-to-cart${cartAdded ? " added" : ""}`}
                  onClick={handleAddToCart}
                >
                  {cartAdded ? "Added to Cart ✓" : "Add to Cart"}
                </button>
              </form>
            )}

            {step === "payment" && selectedEvent && (
              <div className="reservation-form" style={{ position: "relative", zIndex: 2 }}>
                <div
                  style={{
                    background: "rgba(255,255,255,0.1)",
                    borderRadius: 12,
                    padding: 16,
                    marginBottom: 8,
                  }}
                >
                  <p style={{ margin: 0, fontWeight: 700 }}>{selectedEvent.title}</p>
                  <p style={{ margin: 0 }}>
                    {formData.date} · {formData.guests} guest{formData.guests === 1 ? "" : "s"}
                  </p>
                  <p style={{ margin: 0, opacity: 0.85, fontSize: "0.9rem" }}>
                    Final total is calculated securely at checkout.
                  </p>
                </div>

                {!validatePayPalConfig() ? (
                  <p style={{ textAlign: "center", color: "#ffa1a1" }}>
                    PayPal isn't configured yet. Add REACT_APP_PAYPAL_CLIENT_ID to your .env file.
                  </p>
                ) : (
                  <PayPalScriptProvider options={{ clientId: PAYPAL_CONFIG.clientId, currency: "USD" }}>
                    <PayPalButtons
                      style={{ layout: "vertical" }}
                      disabled={isSubmitting}
                      createOrder={async () => {
                        paymentStarted.current = true;
                        try {
                          const order = await createPavilionPayPalOrder({
                            eventId: selectedEvent.id,
                            guests: formData.guests,
                          });
                          return order.orderId;
                        } catch (error) {
                          console.error("createOrder error:", error);
                          setSubmitMessage({
                            type: "error",
                            text: error.message || "Could not start checkout. Please try again.",
                          });
                          throw error;
                        }
                      }}
                      onApprove={async (data) => {
                        setIsSubmitting(true);
                        setSubmitMessage(null);
                        try {
                          const result = await capturePavilionPayPalOrder(data.orderID, {
                            ...formData,
                            eventId: selectedEvent.id,
                            eventTitle: selectedEvent.title,
                          });
                          navigate("/booking-confirmed", {
                            state: {
                              pointsAwarded: result.pointsAwarded,
                              totalCharged: result.amountPaid,
                              bookingCount: 1,
                              newBalance: result.newBalance ?? null,
                            },
                          });
                        } catch (error) {
                          console.error("capture error:", error);
                          setSubmitMessage({
                            type: "error",
                            text: error.message || "Payment could not be completed. Please try again.",
                          });
                        } finally {
                          setIsSubmitting(false);
                        }
                      }}
                      onCancel={() => {
                        if (paymentStarted.current) {
                          setSubmitMessage({ type: "info", text: "Payment canceled." });
                          paymentStarted.current = false;
                        }
                      }}
                      onError={(error) => {
                        console.error("PayPal error:", error);
                        setSubmitMessage({
                          type: "error",
                          text: "Something went wrong with PayPal. Please try again.",
                        });
                      }}
                    />
                  </PayPalScriptProvider>
                )}

                <button type="button" onClick={() => { paymentStarted.current = false; setStep("form"); }} disabled={isSubmitting}>
                  Back to Details
                </button>
              </div>
            )}
          </>
        )}

        {submitMessage && (
          <div
            style={{
              marginTop: 12,
              position: "relative",
              zIndex: 2,
              color:
                submitMessage.type === "success"
                  ? "#3eea94"
                  : submitMessage.type === "info"
                  ? "#ffd27a"
                  : "#ffa1a1",
              textAlign: "center",
              fontWeight: 600,
              textShadow: "0 1px 10px rgba(0, 0, 0, 0.15)",
            }}
          >
            {submitMessage.text}
            {submitMessage.link && (
              <Link
                to={submitMessage.link.to}
                style={{ marginLeft: 8, color: "#d4a853", textDecoration: "underline" }}
              >
                {submitMessage.link.label}
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
